"use client";

import { MetaDot } from "@/components/ui/meta-dot";
import { IconAlertWarningOutline18, IconChatBubbleOutline18, IconPaperPlane2Outline18 } from "nucleo-ui-outline-18";

import UserAvatar from "@/app/(v2)/_components/UserAvatar";
import { useAblyChatClient } from "@/components/providers/AblyChatProvider";
import { Button } from "@/components/ui/button";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ChatMessageEvent, ChatMessageEventType, Message } from "@ably/chat";
import { ChatClientProvider, ChatRoomProvider, useMessages, usePresenceListener, useTyping } from "@ably/chat/react";
import { useAccountWallet } from "@b3dotfun/sdk/global-account/react";
import { Component, ErrorInfo, ReactNode, useEffect, useRef, useState } from "react";

interface CommentsSectionProps {
  /**
   * Unique identifier for the chat room
   * This should be based on the page/resource type and ID
   * e.g., "integration:trigger:evm-event" or "template:workflow-automation"
   */
  roomId: string;

  /**
   * Optional title for the comments section
   */
  title?: string;

  /**
   * Optional description for the comments section
   */
  description?: string;

  /**
   * Optional CSS class name
   */
  className?: string;

  /**
   * Visual variant.
   * - "default": icon + title + description, bordered header
   * - "minimal": clean "Title (N)" header with no icon, used on template detail
   */
  variant?: "default" | "minimal";
}

// Error Boundary for Comments Section
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class CommentsErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("CommentsSection error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 py-8 dark:border-yellow-800 dark:bg-yellow-950/20">
          <IconAlertWarningOutline18 className="h-8 w-8 text-yellow-600 dark:text-yellow-500" />
          <div className="text-center">
            <h3 className="mb-1 text-sm font-semibold text-yellow-900 dark:text-yellow-200">
              Comments temporarily unavailable
            </h3>
            <p className="text-xs text-yellow-700 dark:text-yellow-400">
              We&apos;re having trouble loading the comments. Please try refreshing the page.
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-yellow-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600"
          >
            Refresh page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Component to show typing user with avatar
function TypingUser({ clientId }: { clientId: string }) {
  const isAnonymous = clientId.startsWith("anon_") || clientId.startsWith("client-");

  return (
    <div className="inline-flex items-center gap-1.5">
      {!isAnonymous ? (
        <>
          <UserAvatar b3GlobalId={clientId} size={16} includeAvatar includeUsername={false} />
          <UserAvatar
            b3GlobalId={clientId}
            size={0}
            includeAvatar={false}
            includeUsername
            textClassName="text-xs font-medium text-ds-text-secondary"
          />
        </>
      ) : (
        <>
          <div className="bg-ds-neutral-300 text-ds-text-subtle dark:bg-ds-neutral-700 dark:text-ds-text-disabled text-3xs flex h-4 w-4 items-center justify-center rounded-full font-medium">
            A
          </div>
          <span className="text-ds-text-secondary text-xs font-medium">Anonymous</span>
        </>
      )}
    </div>
  );
}

function CustomMessage({ message }: { message: Message }) {
  const isAnonymous = message.clientId.startsWith("anon_") || message.clientId.startsWith("client-");

  return (
    <div className="flex gap-3 py-4 first:pt-0">
      <div className="flex-shrink-0">
        {!isAnonymous ? (
          <UserAvatar b3GlobalId={message.clientId} size={36} includeAvatar includeUsername={false} />
        ) : (
          <div className="bg-ds-neutral-300 text-ds-text-subtle dark:bg-ds-neutral-700 dark:text-ds-text-disabled flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium">
            A
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center gap-1">
          {!isAnonymous ? (
            <UserAvatar
              b3GlobalId={message.clientId}
              size={0}
              includeAvatar={false}
              includeUsername
              textClassName="text-sm font-semibold text-blue-600 dark:text-blue-400"
            />
          ) : (
            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">Anonymous</span>
          )}
          <MetaDot />
          <span className="label-xs">{new Date(message.createdAt).toLocaleTimeString()}</span>
        </div>

        <div className="text-ds-text-secondary text-sm">{message.text}</div>
      </div>
    </div>
  );
}

function MessageComposer({ variant = "default" }: { variant?: "default" | "minimal" }) {
  const [message, setMessage] = useState("");
  const { sendMessage } = useMessages();
  const { keystroke, stop } = useTyping();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    try {
      await sendMessage({ text: message });
      setMessage("");
      await stop();
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setMessage(newValue);

    if (newValue.trim().length > 0) {
      await keystroke();
    } else {
      await stop();
    }
  };

  if (variant === "minimal") {
    return (
      <form onSubmit={handleSubmit} className="space-y-3">
        <Textarea
          value={message}
          onChange={handleChange}
          placeholder="Type your comment..."
          className="border-ds-border text-ds-text-primary placeholder:text-ds-text-disabled focus:border-ds-focus-brand focus:ring-ds-focus-brand/20 bg-ds-surface-card min-h-[120px] w-full resize-none rounded-xl p-4 text-sm leading-normal focus:ring-1"
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <div className="flex justify-end">
          <Button type="submit" variant="brand" size="sm" disabled={!message.trim()}>
            Post Comment
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Textarea
        value={message}
        onChange={handleChange}
        placeholder="Type a message..."
        className="border-ds-border bg-ds-surface-card min-h-[80px] w-full resize-none rounded-xl p-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        onKeyDown={e => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
          }
        }}
      />
      <div className="flex justify-end">
        <Button type="submit" disabled={!message.trim()} size="sm">
          <span className="flex items-center gap-3 text-xs">
            <IconPaperPlane2Outline18 className="h-3 w-3" />
            Post
          </span>
        </Button>
      </div>
    </form>
  );
}

function CommentsContent({ variant = "default", title }: { variant?: "default" | "minimal"; title?: string }) {
  const [messagesList, setMessagesList] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { currentlyTyping } = useTyping();
  usePresenceListener(); // Keep presence tracking active

  const { historyBeforeSubscribe } = useMessages({
    listener: (event: ChatMessageEvent) => {
      switch (event.type) {
        case ChatMessageEventType.Created: {
          setMessagesList(prev => [...prev, event.message]);
          break;
        }
        case ChatMessageEventType.Updated: {
          setMessagesList(prev => {
            const index = prev.findIndex(msg => event.message.isSameAs(msg));
            if (index === -1) return prev;
            const updatedArray = prev.slice();
            updatedArray[index] = prev[index].with(event);
            return updatedArray;
          });
          break;
        }
        default: {
          console.log("Unhandled event", event);
        }
      }
    },
  });

  // Load message history when component mounts
  useEffect(() => {
    let ignore = false;

    async function loadHistory() {
      try {
        if (!historyBeforeSubscribe) {
          return;
        }
        const history = await historyBeforeSubscribe({ limit: 1000 });
        if (ignore) return;
        // Reverse the order so oldest messages are at the top
        setMessagesList([...(history.items ?? [])].reverse());
      } catch (error) {
        if (ignore) return;
        console.error("Error loading message history:", error);
      }
    }

    loadHistory();

    return () => {
      ignore = true;
    };
  }, [historyBeforeSubscribe]);

  useEffect(() => {
    // Scroll to bottom of messages container
    const container = messagesEndRef.current?.parentElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messagesList]);

  if (variant === "minimal") {
    return (
      <div className="space-y-5">
        <h2 className="text-ds-text-primary text-base font-semibold">
          {title ?? "Discussion"} ({messagesList.length})
        </h2>

        {messagesList.length > 0 ? (
          <div className="divide-ds-neutral-200 dark:divide-ds-neutral-800 max-h-[400px] space-y-0 divide-y overflow-y-auto">
            {messagesList.map(message => (
              <CustomMessage key={message.serial} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </div>
        ) : null}

        {currentlyTyping.size > 0 && (
          <div className="label-xs flex items-center gap-1.5">
            {Array.from(currentlyTyping).map((clientId, index) => (
              <span key={clientId} className="inline-flex items-center gap-1">
                {index > 0 && <span>,</span>}
                <TypingUser clientId={clientId} />
              </span>
            ))}
            <span>{currentlyTyping.size === 1 ? "is" : "are"} typing...</span>
          </div>
        )}

        <MessageComposer variant="minimal" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messagesList.length > 0 ? (
        <div className="divide-ds-neutral-200 dark:divide-ds-neutral-800 max-h-[400px] space-y-0 divide-y overflow-y-auto">
          {messagesList.map(message => (
            <CustomMessage key={message.serial} message={message} />
          ))}
          <div ref={messagesEndRef} />
        </div>
      ) : null}

      {currentlyTyping.size > 0 && (
        <div className="label-xs flex items-center gap-1.5">
          {Array.from(currentlyTyping).map((clientId, index) => (
            <span key={clientId} className="inline-flex items-center gap-1">
              {index > 0 && <span>,</span>}
              <TypingUser clientId={clientId} />
            </span>
          ))}
          <span>{currentlyTyping.size === 1 ? "is" : "are"} typing...</span>
        </div>
      )}

      <div className={cn(messagesList.length > 0 && "pt-2")}>
        <MessageComposer />
      </div>
    </div>
  );
}

/**
 * Wrapper component that checks if chat client is available
 */
function CommentsSectionWrapper({
  roomId,
  variant,
  title,
}: Pick<CommentsSectionProps, "roomId" | "variant" | "title">) {
  const chatClient = useAblyChatClient();
  const { address: globalAddress } = useAccountWallet();

  // Create a stable key that changes when auth state changes
  // This forces the component to remount and reload messages with correct identity
  const authKey = globalAddress ? `auth-${globalAddress}` : "anon";

  // Show loading state while chat client is initializing
  if (!chatClient) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-ds-text-muted flex items-center gap-2 text-sm">
          <PixelLoader size="sm" />
          <span>Loading chat...</span>
        </div>
      </div>
    );
  }

  // Wrap with ChatClientProvider at point of use rather than at the top-level provider.
  // This keeps the top-level tree structure stable (no conditional provider wrapping)
  // while still satisfying the Ably SDK's requirement for ChatClientProvider ancestry.
  return (
    <ChatClientProvider client={chatClient}>
      <ChatRoomProvider key={authKey} name={roomId}>
        <CommentsContent variant={variant} title={title} />
      </ChatRoomProvider>
    </ChatClientProvider>
  );
}

/**
 * CommentsSection component that provides real-time commenting functionality
 * using Ably Chat SDK hooks directly.
 *
 * Usage:
 * ```tsx
 * <CommentsSection
 *   roomId="integration:trigger:evm-event"
 *   title="Discussion"
 *   description="Ask questions and share insights about this integration"
 * />
 * ```
 */
export function CommentsSection({
  roomId,
  title = "Comments",
  description,
  className,
  variant = "default",
}: CommentsSectionProps) {
  if (variant === "minimal") {
    return (
      <div className={cn(className)}>
        <CommentsErrorBoundary>
          <CommentsSectionWrapper roomId={roomId} variant="minimal" title={title} />
        </CommentsErrorBoundary>
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {/* Header */}
      <div className="border-ds-border-default border-b pb-4">
        <div className="flex items-center gap-3">
          <IconChatBubbleOutline18 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h3 className="text-ds-text-primary text-lg font-semibold">{title}</h3>
            {description && <p className="body-sm">{description}</p>}
          </div>
        </div>
      </div>
      {/* Chat Room with Error Boundary */}
      <div className="pt-4">
        <CommentsErrorBoundary>
          <CommentsSectionWrapper roomId={roomId} />
        </CommentsErrorBoundary>
      </div>
    </div>
  );
}
